import { useTranslation } from "react-i18next";
import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Upload,
  Loader2,
  FileText,
  AlertTriangle,
  X,
  Plus,
  Search,
  PencilLine,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
} from "lucide-react";
import { useLocation } from "wouter";
import { ProductCatalogCard } from "@/components/products/ProductCatalogCard";
import { AiRerankLoading } from "@/components/products/AiRerankLoading";
import { AiRerankLoadingLto } from "@/components/products/AiRerankLoadingLto";
import { ListProcessingLoading } from "@/components/products/ListProcessingLoading";
import { formatCentsAsDollars } from "@/lib/price-utils";
import { useProductSummary } from "@/hooks/use-product-summary";
import { useProductSearch } from "@/hooks/use-product-search";
import { useProductsLookup } from "@/hooks/use-products";
import { useHajocaInventory, applyLiveInventory } from "@/hooks/use-hajoca-inventory";
import { useCreateOrder } from "@/requests/orders/create-order";
import { usePostPhoto } from "@/requests/list/post-photo";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { useToast } from "@/hooks/use-toast";
import { parseApiError } from "@/lib/api-error";
import { userErrorMessage } from "@/lib/error-messages";
import type { Product } from "@/types/product";
import { useFractionTransform } from "@/hooks/use-fraction-transform";
import { consumePendingFile } from "@/lib/pending-file";
import { consumePendingListText } from "@/lib/pending-list-text";
import { TextToOrderDialog } from "@/components/orders/TextToOrderDialog";
import { useProductEvents } from "@/hooks/use-product-events";
import { usePcOrderScope } from "@/components/pc/PcOrderScopeProvider";
import { PcOrderingForBanner } from "@/components/pc/PcOrderingForBanner";
import { usePcHomeRoute } from "@/hooks/use-will-call";
import { MAX_PDF_SIZE_BYTES, MAX_IMAGE_SIZE_BYTES } from "@shared/upload-constants";
import type { ExtractionStage } from "@shared/list-progress";
import { QuantityInput } from "@/components/ui/quantity-input";
import { cn } from "@/lib/utils";

/**
 * Keep the processing screen up until this many leading items have finished
 * re-ranking, so the review page reveals with its first results already
 * ranked instead of visibly cascading in.
 */
const RESULTS_REVEAL_COUNT = 2;
/**
 * Safety net: never trap the user on the processing screen if a leading item's
 * search stalls — reveal the review page after this long regardless.
 */
const RESULTS_REVEAL_MAX_WAIT_MS = 20000;
/**
 * How many product matches each line item offers. Roughly a page of three is
 * visible at once; the rest are reached with the strip's paging arrows.
 */
const RESULTS_PER_ITEM = 5;
/** Cards visible in the strip at once — sets the mobile height cap. */
const STRIP_VISIBLE_CARDS = 3;

interface ListItem {
  /**
   * Stable client-side identity for this row. Used as the React key so
   * per-item state (edited-search flag, active search term) stays with the
   * item — not its position — when rows are removed.
   */
  id: string;
  quantity: number;
  searchTerm: string;
  selectedProductId?: string;
}

/**
 * Stand-ins for the file identity in list analytics events, which require a name
 * and type. Text entry has no file, so it reports itself with these instead of
 * changing the event schemas.
 */
const TEXT_ENTRY_FILE_NAME = "hand-entered text";
const TEXT_ENTRY_FILE_TYPE = "text/plain";

let nextListItemId = 0;
const createListItemId = () => `list-item-${++nextListItemId}`;

export default function List() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const pcScope = usePcOrderScope();
  const pcHomeRoute = usePcHomeRoute();

  // PC users must be acting for a contractor here — the list, its pricing,
  // and the order it creates are all contractor-scoped. Unbound (e.g. a
  // direct URL), send them to the order page to pick one first. Wait for the
  // sticky restore so a normal page load doesn't bounce.
  useEffect(() => {
    if (pcScope && pcScope.restored && !pcScope.contractor) {
      navigate("/pc/orders/new");
    }
  }, [pcScope, navigate]);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  // Hand-entered list text being processed, the text-entry analog of
  // `uploadedFile` — keeps the upload UI from flashing back on an empty result.
  const [submittedText, setSubmittedText] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Real extraction stage streamed from the upload endpoint, used to drive the
  // processing checklist. Null until the first stage event arrives.
  const [extractionStage, setExtractionStage] = useState<ExtractionStage | null>(null);
  const [listItems, setListItems] = useState<ListItem[]>([]);
  // Number of items whose quantity was capped to the max during extraction.
  // Drives the orange "quantity limited" warning at the top of the list.
  const [clampedCount, setClampedCount] = useState(0);
  // Text-entry dialog reopened from the review screen's "Retry Text" action.
  const [retryTextDialogOpen, setRetryTextDialogOpen] = useState(false);

  // Progressive AI re-ranking ("AI Enhanced LTO"): re-rank each line item's
  // results with an LLM, one item at a time from top to bottom. Only the item at
  // `rerankActiveIndex` runs; each item's results stay hidden until its own
  // re-rank settles, then the cascade advances to the next item.
  const [rerankActiveIndex, setRerankActiveIndex] = useState(0);
  // Force-reveal the review page once the safety timeout fires, even if the
  // leading items haven't finished re-ranking yet.
  const [revealTimedOut, setRevealTimedOut] = useState(false);

  const handleRerankAdvance = useCallback((index: number) => {
    setRerankActiveIndex((prev) => (index === prev ? prev + 1 : prev));
  }, []);

  const postPhotoMutation = usePostPhoto();
  const { toast } = useToast();
  const isTextListEnabled = useFeatureFlagEnabled("text-lto") ?? false;

  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const { capture } = useProductEvents({ component: "list" });
  const listSessionIdRef = useRef(
    `list_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
  );
  const uploadAttemptRef = useRef(0);

  const submitFile = useCallback(async (file: File) => {
    const isPdfFile = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    const maxSize = isPdfFile ? MAX_PDF_SIZE_BYTES : MAX_IMAGE_SIZE_BYTES;
    if (file.size > maxSize) {
      toast({
        title: t(isPdfFile ? "pages.list.pdfTooLarge" : "pages.list.imageTooLarge"),
        description: `This ${isPdfFile ? "PDF" : "image"} is ${(file.size / 1024 / 1024).toFixed(1)} MB. The maximum size is ${maxSize / 1024 / 1024} MB — please upload a smaller ${isPdfFile ? "PDF or a photo of your list" : "image"}.`,
        variant: "destructive",
      });
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploadedFile(file);
    setIsProcessing(true);
    setExtractionStage(null);
    setListItems([]);
    setClampedCount(0);
    setRerankActiveIndex(0);
    setRevealTimedOut(false);
    uploadAttemptRef.current += 1;

    capture({
      event: "list_upload_started",
      context: {
        listSessionId: listSessionIdRef.current,
        uploadAttempt: uploadAttemptRef.current,
        fileName: file.name,
        fileType: file.type,
      },
    });

    try {
      const response = await postPhotoMutation.mutateAsync({
        file,
        onStage: setExtractionStage,
      });

      capture({
        event: "list_photo_analyzed",
        context: {
          listSessionId: listSessionIdRef.current,
          uploadAttempt: uploadAttemptRef.current,
          fileName: file.name,
          fileType: file.type,
          itemCount: response.items?.length ?? 0,
          extractedSearchTerms: response.items?.map((i) => i.searchTerm) ?? [],
        },
      });

      setClampedCount(response.clampedCount ?? 0);

      if (response.items && response.items.length > 0) {
        setListItems(
          response.items.map((item) => ({ ...item, id: createListItemId() })),
        );
      } else {
        toast({
          title: t("pages.list.noProductsFound"),
          description: t("pages.list.weCouldnTExtract"),
          variant: "default",
        });
      }
    } catch (error: any) {
      const parsed = parseApiError(error);
      const errorMessage = parsed.message;
      capture({
        event: "list_upload_failed",
        context: {
          listSessionId: listSessionIdRef.current,
          uploadAttempt: uploadAttemptRef.current,
          fileName: file.name,
          fileType: file.type,
          error: errorMessage,
        },
      });
      setUploadedFile(null);
      setListItems([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
      const code = error?.error;
      let title: string;
      let description: string;
      if (code === "NETWORK_ERROR") {
        title = t("pages.list.connectionProblem");
        description = t("pages.list.weCouldnTReach");
      } else if (code === "UPLOAD_INTERRUPTED") {
        title = t("pages.list.uploadInterrupted");
        description = t("pages.list.theUploadWasInterrupted");
      } else {
        title = isPdf ? "Couldn't process PDF" : "Couldn't process image";
        description = userErrorMessage(parsed);
      }
      toast({ title, description, variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  }, [postPhotoMutation, toast, capture]);

  // Hand-entered list text: same pipeline as an upload, joining it at the point
  // raw text is turned into search terms (POST /api/list/text).
  const submitText = useCallback(async (text: string) => {
    setSubmittedText(text);
    setIsProcessing(true);
    setExtractionStage(null);
    setListItems([]);
    setClampedCount(0);
    setRerankActiveIndex(0);
    setRevealTimedOut(false);
    uploadAttemptRef.current += 1;

    capture({
      event: "list_upload_started",
      context: {
        listSessionId: listSessionIdRef.current,
        uploadAttempt: uploadAttemptRef.current,
        fileName: TEXT_ENTRY_FILE_NAME,
        fileType: TEXT_ENTRY_FILE_TYPE,
      },
    });

    try {
      const response = await postPhotoMutation.mutateAsync({
        text,
        onStage: setExtractionStage,
      });

      capture({
        event: "list_photo_analyzed",
        context: {
          listSessionId: listSessionIdRef.current,
          uploadAttempt: uploadAttemptRef.current,
          fileName: TEXT_ENTRY_FILE_NAME,
          fileType: TEXT_ENTRY_FILE_TYPE,
          itemCount: response.items?.length ?? 0,
          extractedSearchTerms: response.items?.map((i) => i.searchTerm) ?? [],
        },
      });

      setClampedCount(response.clampedCount ?? 0);

      if (response.items && response.items.length > 0) {
        setListItems(
          response.items.map((item) => ({ ...item, id: createListItemId() })),
        );
      } else {
        toast({
          title: t("pages.list.noProductsFound"),
          description: t("pages.list.weCouldnTExtract"),
          variant: "default",
        });
      }
    } catch (error: any) {
      const errorMessage = error?.error || error?.message || "Unknown error";
      capture({
        event: "list_upload_failed",
        context: {
          listSessionId: listSessionIdRef.current,
          uploadAttempt: uploadAttemptRef.current,
          fileName: TEXT_ENTRY_FILE_NAME,
          fileType: TEXT_ENTRY_FILE_TYPE,
          error: errorMessage,
        },
      });
      setSubmittedText(null);
      setListItems([]);
      const code = error?.error;
      let title: string;
      let description: string;
      if (code === "NETWORK_ERROR") {
        title = t("pages.list.connectionProblem");
        description = t("pages.list.weCouldnTReach");
      } else if (code === "UPLOAD_INTERRUPTED") {
        title = t("pages.list.uploadInterrupted");
        description = t("pages.list.theUploadWasInterrupted");
      } else {
        title = t("pages.list.couldnTProcessList");
        description = errorMessage;
      }
      toast({ title, description, variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  }, [postPhotoMutation, toast, capture, t]);

  // Auto-process a file passed from PhotoToOrderPanel via in-memory transfer
  useEffect(() => {
    const file = consumePendingFile();
    if (!file) return;
    submitFile(file);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-process list text passed from TextToOrderDialog. Waits for the flag to
  // resolve rather than consuming on mount, so a still-loading flag can't drop
  // the pending text.
  useEffect(() => {
    if (!isTextListEnabled) return;
    const text = consumePendingListText();
    if (!text) return;
    submitText(text);
  }, [isTextListEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFileUpload = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      await submitFile(file);
    },
    [submitFile]
  );

  // Track selected products with their details for summary calculation.
  // Keyed by the item's stable `id` (not position) so removing a row never
  // requires reindexing this map - and carries the searchId/query that
  // actually produced the product, so list_start_order can report a real,
  // non-reconstructed link instead of leaving it to be guessed later.
  const [selectedProductsMap, setSelectedProductsMap] = useState<
    Map<
      string,
      {
        productId: string;
        quantity: number;
        unitPrice?: number;
        searchId?: string;
        query?: string;
      }
    >
  >(new Map());

  const createOrderMutation = useCreateOrder();

  // Full product records (hajocaProductId, description) for whatever's
  // currently selected, so handleCreateOrder can send a complete order -
  // list rows only track productId/quantity/unitPrice.
  const selectedProductIds = useMemo(
    () => Array.from(selectedProductsMap.values()).map((p) => p.productId),
    [selectedProductsMap],
  );
  const { products: selectedFullProducts } = useProductsLookup(
    selectedProductIds,
    selectedProductIds.length > 0,
  );

  // Clear the current results so the page can accept a new submission. Shared by
  // both retry paths; `keepSubmittedText` retains the text so the retry dialog
  // can prefill it while still dropping the review list behind the dialog.
  const resetForRetry = useCallback((keepSubmittedText = false) => {
    capture({
      event: "list_retry_upload",
      context: {
        listSessionId: listSessionIdRef.current,
        uploadAttempt: uploadAttemptRef.current,
        previousItemCount: listItems.length,
      },
    });
    setUploadedFile(null);
    if (!keepSubmittedText) setSubmittedText(null);
    setListItems([]);
    setClampedCount(0);
    setRerankActiveIndex(0);
    setRevealTimedOut(false);
    setSelectedProductsMap(new Map());
    setItemsWithNoProducts(new Set());
    setIsProcessing(false);
  }, [capture, listItems.length]);

  // Retry with a file: drop everything and return to the upload screen.
  const handleRetryUpload = useCallback(() => {
    resetForRetry();
  }, [resetForRetry]);

  // Retry with text: reopen the entry dialog prefilled with what was submitted,
  // so the user can correct their list instead of retyping it.
  const handleRetryText = useCallback(() => {
    setRetryTextDialogOpen(true);
  }, []);

  const handleRetryTextSubmit = useCallback((text: string) => {
    resetForRetry(true);
    submitText(text);
  }, [resetForRetry, submitText]);

  const handleAddRow = useCallback(() => {
    setListItems((prev) => [
      ...prev,
      { id: createListItemId(), quantity: 1, searchTerm: "" },
    ]);
  }, []);

  const handleRemoveRow = useCallback((indexToRemove: number) => {
    const removedItem = listItems[indexToRemove];
    capture({
      event: "list_item_deleted",
      context: {
        listSessionId: listSessionIdRef.current,
        itemId: removedItem?.id,
        itemIndex: indexToRemove,
        remainingCount: listItems.length - 1,
      },
    });
    // Keep the cascade pointed at the same item: removing a row above the
    // active one shifts it up by one. Removing the active row itself keeps the
    // index, so the next item (which slides into the slot) becomes active.
    setRerankActiveIndex((prev) => (indexToRemove < prev ? prev - 1 : prev));

    setListItems((prev) => prev.filter((_, index) => index !== indexToRemove));

    // Both maps are keyed by the item's stable id, not position, so removing
    // a row is just dropping its entry - no index shifting to reconcile.
    if (removedItem) {
      setSelectedProductsMap((prevMap) => {
        if (!prevMap.has(removedItem.id)) return prevMap;
        const newMap = new Map(prevMap);
        newMap.delete(removedItem.id);
        return newMap;
      });
      setItemsWithNoProducts((prevSet) => {
        if (!prevSet.has(removedItem.id)) return prevSet;
        const newSet = new Set(prevSet);
        newSet.delete(removedItem.id);
        return newSet;
      });
    }
  }, [capture, listItems]);

  // Collect all selected products with their quantities and prices for summary
  const selectedProductsForSummary = useMemo(() => {
    return Array.from(selectedProductsMap.values()).map((p) => ({
      quantity: p.quantity,
      unitPrice: p.unitPrice,
    }));
  }, [selectedProductsMap]);

  // Calculate summary stats using the hook
  const summary = useProductSummary({
    selectedProducts: selectedProductsForSummary,
    showPricing: true,
  });

  // Track which list items have no products found, keyed by the item's
  // stable id (not position) for the same reason as selectedProductsMap.
  const [itemsWithNoProducts, setItemsWithNoProducts] = useState<Set<string>>(
    new Set()
  );

  const summaryStats = useMemo(() => {
    const totalItems = listItems.length;
    return {
      totalItems,
      totalCost: summary.totalValueCents,
    };
  }, [listItems, summary.totalValueCents]);

  // Every item has either landed on a selected product or been confirmed as
  // having no matches — only then is the total cost final. Until that point
  // it's still climbing as items resolve, so the total shows a spinner
  // instead of a jumping number.
  const totalCostSettled =
    listItems.length > 0 &&
    itemsWithNoProducts.size + selectedProductsMap.size >= listItems.length;

  const handleCreateOrder = useCallback(async () => {
    // Selections in the order their rows appear on the page — the order the
    // lines were parsed from the uploaded list. The map's own iteration order
    // is insertion order (when each row's search happened to resolve), which
    // diverges from the list whenever rows resolve out of order, e.g. a
    // "no products" row the user fixes last.
    const selectedEntries = listItems.flatMap((item) => {
      const selection = selectedProductsMap.get(item.id);
      return selection ? [[item.id, selection] as const] : [];
    });
    const fullProductsById = new Map(
      selectedFullProducts.map((product) => [product.uid, product]),
    );
    const orderProducts = selectedEntries.map(([, p]) => {
      const fullProduct = fullProductsById.get(p.productId);
      return {
        productId: p.productId,
        hajocaProductId: fullProduct?.hajocaProductId,
        description: fullProduct?.description,
        quantity: p.quantity,
        unitPrice: p.unitPrice,
      };
    });

    if (orderProducts.length === 0) {
      toast({
        title: t("pages.list.noProductsSelected"),
        description: t("pages.list.selectAtLeastOne"),
        variant: "destructive",
      });
      return;
    }

    const invalidProducts = orderProducts.filter((p) => !p.quantity || p.quantity < 1);
    if (invalidProducts.length > 0) {
      toast({
        title: t("pages.list.invalidQuantity"),
        description: t("pages.list.allSelectedProductsMust"),
        variant: "destructive",
      });
      return;
    }

    capture({
      event: "list_start_order",
      context: {
        listSessionId: listSessionIdRef.current,
        uploadAttempt: uploadAttemptRef.current,
        productCount: orderProducts.length,
        productIds: orderProducts.map((p) => p.productId),
        // Real, non-reconstructed query -> product link per line item: the
        // exact searchId/query that was active when this product got locked
        // in (auto-default or explicit click), keyed by the item's stable id.
        lineItems: selectedEntries.map(([itemId, p]) => ({
          itemId,
          productId: p.productId,
          searchId: p.searchId ?? null,
          query: p.query ?? null,
        })),
        totalItems: listItems.length,
        itemsWithNoProducts: itemsWithNoProducts.size,
        estimatedTotalCents: summaryStats.totalCost,
      },
    });

    try {
      const newOrder = await createOrderMutation.mutateAsync({
        title: `Order from List - ${new Date().toLocaleDateString()}`,
        description: `Order created from list with ${orderProducts.length} items`,
        fulfillmentType: "delivery",
        products: orderProducts,
      });

      if (newOrder && "uid" in newOrder && newOrder.uid) {
        navigate(`/orders/edit/${newOrder.uid}?from=list`);
      } else {
        navigate(pcScope ? pcHomeRoute : "/orders");
      }
    } catch (error) {
      console.error("Failed to create order:", error);
      toast({
        title: t("pages.list.failedToCreateOrder"),
        description: t("pages.list.somethingWentWrongPlease"),
        variant: "destructive",
      });
    }
  }, [selectedProductsMap, selectedFullProducts, createOrderMutation, navigate, pcScope, pcHomeRoute, capture, toast, listItems, itemsWithNoProducts.size, summaryStats.totalCost]);

  // Gate the review page reveal on the leading items finishing their re-rank.
  // `rerankActiveIndex` advances past an item once its re-rank settles, so it
  // reaching N means the first N items are done.
  const revealGateActive = listItems.length > 0;
  const leadingItemsReranked =
    rerankActiveIndex >= Math.min(RESULTS_REVEAL_COUNT, listItems.length);
  const resultsReady =
    !revealGateActive || leadingItemsReranked || revealTimedOut;
  // Keep the processing screen up during extraction and while we wait for the
  // leading items to re-rank. The item cards still mount (hidden) underneath so
  // their re-ranks actually run during this wait.
  const showLoadingScreen = isProcessing || !resultsReady;

  // Safety net so a stalled leading search can't trap the user on the loading
  // screen forever.
  useEffect(() => {
    if (!revealGateActive || leadingItemsReranked || revealTimedOut) return;
    const id = setTimeout(
      () => setRevealTimedOut(true),
      RESULTS_REVEAL_MAX_WAIT_MS,
    );
    return () => clearTimeout(id);
  }, [revealGateActive, leadingItemsReranked, revealTimedOut]);

  // If no file uploaded yet, show upload UI
  if (!uploadedFile && !submittedText && !isProcessing && listItems.length === 0) {
    return (
      <Layout headerContent={
        <div className="flex items-center gap-2 text-sm">
          <span className="text-muted-foreground">{t("common.orders")}</span>
          <span className="text-muted-foreground">/</span>
          <span className="font-semibold">{t("pages.list.uploadList")}</span>
        </div>
      }>
        <PcOrderingForBanner />
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
          <div className="text-center space-y-4">
            <h1 className="text-3xl font-bold">{t("pages.list.uploadAList")}</h1>
            <p className="text-muted-foreground max-w-md">
              {t("pages.list.uploadAPhotoScreenshot")}
            </p>
          </div>
          <div className="flex flex-col items-center gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.pdf"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              size="lg"
              className="h-16 text-lg px-8"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="h-6 w-6 mr-3" />
              {t("pages.list.uploadImageOrPdf")}
            </Button>
            <p className="text-xs text-muted-foreground">
              {t("pages.list.supportsJpegPngGif")}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  const processingHeader = (
    <div className="flex items-center gap-2 text-sm">
      <span className="text-muted-foreground">{t("common.orders")}</span>
      <span className="text-muted-foreground">/</span>
      <span className="font-semibold">{t("pages.list.uploadList")}</span>
    </div>
  );

  // Show review list. While extraction runs (or the leading items are still
  // re-ranking) the processing screen is shown on top and the review content is
  // kept mounted but hidden, so the item cards can run their re-ranks and the
  // page reveals with its first results already ranked.
  return (
    <Layout headerContent={showLoadingScreen ? processingHeader : undefined}>
      <PcOrderingForBanner />
      {showLoadingScreen && (
        <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6">
          <ListProcessingLoading
            stage={extractionStage}
            searching={listItems.length > 0}
          />
        </div>
      )}
      <div className={cn("space-y-6", showLoadingScreen && "hidden")}>
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">{t("pages.list.reviewList")}</h1>
        </div>

        {/* Affixed Summary Row - Desktop: top, Mobile: bottom */}
        <div className="sticky bottom-0 md:bottom-auto md:top-0 z-10 bg-background md:bg-background/95 md:backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-4 md:pb-0 md:pt-4">
          <Card className="shadow-lg">
            <CardContent className="p-4 md:p-6">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="grid grid-cols-2 gap-4 flex-1">
                  <div>
                    <p className="text-sm text-muted-foreground">{t("pages.list.totalItems")}</p>
                    <p className="text-2xl font-bold">
                      {summaryStats.totalItems}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">{t("pages.list.totalCost")}</p>
                    <p className="text-2xl font-bold">
                      {totalCostSettled ? (
                        `$${formatCentsAsDollars(summaryStats.totalCost)}`
                      ) : (
                        <span className="[animation:pulse-deep_2.5s_ease-in-out_infinite]">—</span>
                      )}
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={handleCreateOrder}
                    size="lg"
                    className="h-12 flex-1 md:flex-none"
                    disabled={!totalCostSettled}
                  >
                    {t("pages.list.startOrder")}
                  </Button>
                  <Button
                    onClick={handleRetryUpload}
                    variant="outline"
                    size="lg"
                    className="h-12 flex-1 md:flex-none"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    {isTextListEnabled
                      ? t("pages.list.retryFileUpload")
                      : t("pages.list.retryUpload")}
                  </Button>
                  {isTextListEnabled && (
                    <Button
                      onClick={handleRetryText}
                      variant="outline"
                      size="lg"
                      className="h-12 flex-1 md:flex-none"
                    >
                      <PencilLine className="h-4 w-4 mr-2" />
                      {t("pages.list.retryTextEntry")}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alert for quantities that were capped to the maximum */}
        {clampedCount > 0 && (
          <Alert className="border-orange-500/50 text-orange-700 dark:text-orange-400 [&>svg]:text-orange-600 dark:[&>svg]:text-orange-400">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("pages.list.quantityLimitedTitle")}</AlertTitle>
            <AlertDescription>
              {clampedCount === 1
                ? t("pages.list.quantityLimited1Item")
                : t("pages.list.quantityLimitedItems", { count: clampedCount })}
            </AlertDescription>
          </Alert>
        )}

        {/* Alert for items with no products found */}
        {itemsWithNoProducts.size > 0 && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>{t("pages.list.someSearchesReturnedNo")}</AlertTitle>
            <AlertDescription>
              {itemsWithNoProducts.size === 1
                ? t("pages.list.1Item")
                : `${itemsWithNoProducts.size} items`}{" "}
              {itemsWithNoProducts.size === 1 ? "has" : "have"} {t("pages.list.noProductsFoundPlease")}
            </AlertDescription>
          </Alert>
        )}

        {/* List Items */}
        <div className="space-y-6">
          {listItems.map((item, index) => (
            <ListItemCard
              key={item.id}
              item={item}
              index={index}
              listSessionId={listSessionIdRef.current}
              rerankActiveIndex={rerankActiveIndex}
              onRerankAdvance={handleRerankAdvance}
              onItemChange={(updatedItem) => {
                const newItems = [...listItems];
                newItems[index] = updatedItem;
                setListItems(newItems);
              }}
              onSelectedProductChange={(productId, quantity, unitPrice, searchId, query) => {
                setSelectedProductsMap((prev) => {
                  const newMap = new Map(prev);
                  const key = item.id; // stable across row removal, unlike position
                  if (productId) {
                    newMap.set(key, { productId, quantity, unitPrice, searchId, query });
                  } else {
                    newMap.delete(key);
                  }
                  return newMap;
                });
              }}
              onNoProductsFound={(hasNoProducts) => {
                // A settled search with no results also invalidates any
                // earlier selection for this row (e.g. a matched row the
                // user re-searched into a dead end) — otherwise the stale
                // product would still be submitted while the row shows
                // "No products found".
                if (hasNoProducts) {
                  setSelectedProductsMap((prev) => {
                    if (!prev.has(item.id)) return prev;
                    const newMap = new Map(prev);
                    newMap.delete(item.id);
                    return newMap;
                  });
                }
                setItemsWithNoProducts((prev) => {
                  // Bail out (same reference) when nothing changed — the child
                  // re-reports on every render, and always returning a new Set
                  // would re-render in an infinite loop.
                  if (prev.has(item.id) === hasNoProducts) return prev;
                  const newSet = new Set(prev);
                  if (hasNoProducts) {
                    newSet.add(item.id);
                  } else {
                    newSet.delete(item.id);
                  }
                  return newSet;
                });
              }}
              onRemove={handleRemoveRow}
              canRemove={listItems.length > 1}
            />
          ))}
          {/* Add Row Button */}
          <Button
            onClick={handleAddRow}
            variant="outline"
            size="lg"
            className="w-full h-12"
          >
            <Plus className="h-5 w-5 mr-2" />
            {t("pages.list.addItem")}
          </Button>
        </div>
      </div>
      {isTextListEnabled && (
        <TextToOrderDialog
          open={retryTextDialogOpen}
          onOpenChange={setRetryTextDialogOpen}
          initialText={submittedText ?? ""}
          onSubmit={handleRetryTextSubmit}
        />
      )}
    </Layout>
  );
}

interface ListItemCardProps {
  item: ListItem;
  index: number;
  listSessionId: string;
  /** Index of the item currently allowed to re-rank; items above it are done. */
  rerankActiveIndex: number;
  /** Called when this item (as the active one) finishes re-ranking. */
  onRerankAdvance: (index: number) => void;
  onItemChange: (item: ListItem) => void;
  onSelectedProductChange?: (
    productId: string,
    quantity: number,
    unitPrice?: number,
    searchId?: string,
    query?: string
  ) => void;
  onNoProductsFound?: (hasNoProducts: boolean) => void;
  onRemove?: (index: number) => void;
  canRemove?: boolean;
}

function ListItemCard({
  item,
  index,
  listSessionId,
  rerankActiveIndex,
  onRerankAdvance,
  onItemChange,
  onSelectedProductChange,
  onNoProductsFound,
  onRemove,
  canRemove = false,
}: ListItemCardProps) {
  const { t } = useTranslation();
  const [quantity, setQuantity] = useState(item.quantity);
  const [searchInput, setSearchInput] = useState(item.searchTerm);
  const [activeSearchTerm, setActiveSearchTerm] = useState(item.searchTerm);
  const [selectedProductId, setSelectedProductId] = useState<
    string | undefined
  >(item.selectedProductId);
  // Once the user manually searches this item (vs. the term extracted from the
  // uploaded photo/PDF), behave like the regular Product Catalog "AI Enhanced"
  // search — Sonnet model — instead of the fast progressive LTO variant/cascade.
  const [isUserEditedSearch, setIsUserEditedSearch] = useState(false);
  // Text the box held before the server correction rewrote it.
  const [correctionOriginal, setCorrectionOriginal] = useState<string | null>(
    null
  );
  // Sends the next request uncorrected, set when the user reverts a correction.
  const [skipCorrection, setSkipCorrection] = useState(false);
  const { capture } = useProductEvents({ component: "ListItemCard" });
  // Links a correction event to the revert event that may follow it.
  const correctionTransformationIdRef = React.useRef<string | null>(null);
  // The correction the card wrote into the box, so searching it again is not
  // read as the user editing the term.
  const appliedCorrectionRef = React.useRef<string | null>(null);

  // Rewrites fraction shorthand in the box once typing settles.
  const fraction = useFractionTransform({
    // Puts the fraction in the box; the search waits for Enter or the button.
    onTransform: (transformed) => setSearchInput(transformed),
    // Puts the typed text back and searches it exactly as typed — correction
    // included, since a user keeping their own wording means all of it.
    onRevert: (original) => {
      const trimmedOriginal = original.trim();
      setSearchInput(original);
      setActiveSearchTerm(trimmedOriginal);
      setSkipCorrection(true);
      setSelectedProductId(undefined);
      // Same as performSearch: a changed term drops the row's selection in the
      // parent immediately, so the product picked for the rewritten term
      // cannot ride into an order started from the reverted one.
      if (trimmedOriginal !== item.searchTerm) {
        onSelectedProductChange?.("", quantity);
      }
      onItemChange({
        ...item,
        searchTerm: trimmedOriginal,
        selectedProductId: undefined,
      });
    },
    capture,
  });

  // Manual (click-to-page) navigation of the results strip: horizontal on web,
  // vertical on mobile. We track whether there's room to page in each direction
  // so the arrow affordances only show when they'd actually do something.
  const scrollStripRef = useRef<HTMLDivElement>(null);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  // On mobile we cap the strip to exactly 3 cards' worth of height (measured
  // from the real cards, not a guessed rem value); desktop height is
  // content-driven so this stays undefined there.
  const [stripMaxHeight, setStripMaxHeight] = useState<number | undefined>(
    undefined
  );

  const STRIP_GAP = 16; // gap-4
  const STRIP_PAD = 16; // p-2 top + bottom

  const isDesktopStrip = useCallback(
    () => window.matchMedia("(min-width: 768px)").matches,
    []
  );

  const measureStrip = useCallback(() => {
    const el = scrollStripRef.current;
    if (!el) return;
    // Orientation follows the layout: horizontal on desktop, vertical on mobile.
    const desktop = isDesktopStrip();
    const pos = desktop ? el.scrollLeft : el.scrollTop;
    const max = desktop
      ? el.scrollWidth - el.clientWidth
      : el.scrollHeight - el.clientHeight;
    setCanScrollPrev(pos > 8);
    setCanScrollNext(pos < max - 8);

    if (desktop) {
      setStripMaxHeight(undefined);
      return;
    }
    const cards = Array.from(el.children) as HTMLElement[];
    const visible = cards.slice(0, STRIP_VISIBLE_CARDS);
    if (visible.length === 0) {
      setStripMaxHeight(undefined);
      return;
    }
    const height =
      visible.reduce((sum, c) => sum + c.offsetHeight, 0) +
      STRIP_GAP * (visible.length - 1) +
      STRIP_PAD;
    setStripMaxHeight(height);
  }, [isDesktopStrip]);

  const pageStrip = useCallback(
    (direction: 1 | -1) => {
      const el = scrollStripRef.current;
      if (!el) return;
      const desktop = isDesktopStrip();
      // Advance exactly one card (card size + gap) per click.
      const firstCard = el.children[0] as HTMLElement | undefined;
      const cardSize = desktop
        ? firstCard?.offsetWidth ?? el.clientWidth
        : firstCard?.offsetHeight ?? el.clientHeight;
      const amount = cardSize + STRIP_GAP;
      el.scrollBy(
        desktop
          ? { left: amount * direction, behavior: "smooth" }
          : { top: amount * direction, behavior: "smooth" }
      );
    },
    [isDesktopStrip]
  );

  // Update activeSearchTerm when item.searchTerm changes from parent
  useEffect(() => {
    if (item.searchTerm !== activeSearchTerm) {
      setActiveSearchTerm(item.searchTerm);
      setSearchInput(item.searchTerm);
      fraction.reset();
    }
  }, [item.searchTerm]);

  // Progressive re-rank turn tracking. An item only runs its (re-ranked) search
  // once the cascade reaches it, and stays hidden until then. This queueing only
  // applies to the LTO variant — once the user edits the search term themselves,
  // the cascade gate no longer applies to this item.
  const hasValidTerm = activeSearchTerm.trim().length >= 2;
  const turnGateActive = !isUserEditedSearch;
  const myTurnReached = !turnGateActive || index <= rerankActiveIndex;
  const isActiveTurn = index === rerankActiveIndex;

  // Use the product search hook - only search when activeSearchTerm changes.
  // User-edited searches re-rank like the catalog search (Sonnet model);
  // extracted items use the fast progressive LTO variant.
  const {
    products,
    isLoading: productsLoading,
    isFetching: productsFetching,
    isFetched: productsFetched,
    searchId,
    correctedQuery,
    queryChanges,
  } = useProductSearch({
    searchQuery: activeSearchTerm,
    limit: RESULTS_PER_ITEM,
    enabled: hasValidTerm && myTurnReached,
    llmRerank: true,
    rerankVariant: isUserEditedSearch ? "search" : "lto",
    skipCorrection,
    eventName: "list_search_products",
  });

  // Writes the server's typo/alias correction into the box. This runs even
  // with a fraction rewrite showing — the box has to read as what was actually
  // searched — and the banner below renders the correction instead of the
  // fraction offer, so the two never stack.
  useEffect(() => {
    // Nothing to apply, or the search already ran on the corrected text.
    if (!correctedQuery || correctedQuery === activeSearchTerm) return;
    // The user has typed since this search went out, so leave their text be.
    if (searchInput.trim() !== activeSearchTerm) return;
    // This correction has been applied once already.
    if (appliedCorrectionRef.current === correctedQuery) return;

    appliedCorrectionRef.current = correctedQuery;
    const id = `transform_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    correctionTransformationIdRef.current = id;
    setCorrectionOriginal((prev) => prev ?? activeSearchTerm);
    setSearchInput(correctedQuery);
    // The correction replaces the box wholesale, so any fraction rewrite the
    // hook is still standing in for is gone, and its offer with it.
    fraction.reset();

    const changes = queryChanges ?? [];
    capture({
      event: "query_transformed",
      context: {
        // Pairs this event with the revert that may follow.
        transformationId: id,
        // The term the search went out with.
        rawQuery: activeSearchTerm,
        // The term the server matched on.
        transformedQuery: correctedQuery,
        // Separates a spelling fix from an abbreviation being expanded.
        transformationType: changes.some((c) => c.type === "typo")
          ? "typo_correction"
          : "alias_expansion",
        // The individual words that changed.
        queryChanges: changes,
      },
    });
    // Runs on the correction and the term behind it, not on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correctedQuery, activeSearchTerm]);

  // Puts the term the user typed back and searches it uncorrected.
  const handleRevertCorrection = useCallback(() => {
    if (correctionOriginal === null) return;
    capture({
      event: "query_transform_reverted",
      context: {
        // Matches the id sent with the correction being undone.
        transformationId: correctionTransformationIdRef.current ?? "unknown",
        // The correction the box was showing.
        transformedQuery: searchInput,
        // The term going back in.
        revertedQuery: correctionOriginal,
      },
    });
    setSearchInput(correctionOriginal);
    setActiveSearchTerm(correctionOriginal);
    setSkipCorrection(true);
    setSelectedProductId(undefined);
    onItemChange({
      ...item,
      searchTerm: correctionOriginal,
      selectedProductId: undefined,
    });
    setCorrectionOriginal(null);
    appliedCorrectionRef.current = null;
    correctionTransformationIdRef.current = null;
  }, [correctionOriginal, searchInput, item, onItemChange, capture]);

  const displaySearchTerm =
    correctedQuery && correctedQuery !== activeSearchTerm
      ? correctedQuery
      : activeSearchTerm;

  // Advance the cascade once this item (as the active one) settles. Items with
  // no valid search term advance immediately so they don't stall the queue.
  const rerankAdvancedRef = useRef(false);
  useEffect(() => {
    rerankAdvancedRef.current = false;
  }, [isActiveTurn, activeSearchTerm]);
  useEffect(() => {
    if (!isActiveTurn || rerankAdvancedRef.current) return;
    if (!hasValidTerm) {
      rerankAdvancedRef.current = true;
      onRerankAdvance(index);
      return;
    }
    if (productsFetched && !productsFetching) {
      rerankAdvancedRef.current = true;
      onRerankAdvance(index);
    }
  }, [
    isActiveTurn,
    hasValidTerm,
    productsFetched,
    productsFetching,
    index,
    onRerankAdvance,
  ]);

  // The active item shows the full "AI thinking" state and items still waiting
  // their turn show a lighter queued placeholder. Once the user edits the search
  // themselves, these no longer apply — it instead shows the same "AI Enhanced"
  // indicator the Product Catalog search uses.
  const showActiveRerank =
    turnGateActive && hasValidTerm && isActiveTurn && (!productsFetched || productsFetching);
  const showWaitingRerank = turnGateActive && hasValidTerm && !myTurnReached;
  const showSearchAiRerank =
    isUserEditedSearch && hasValidTerm && productsFetching;

  const topProducts = useMemo(() => {
    if (!products || !Array.isArray(products)) return [];
    return products.slice(0, RESULTS_PER_ITEM);
  }, [products]);

  const searchSettled = turnGateActive
    ? myTurnReached && productsFetched && !productsFetching
    : !productsLoading;

  // Live pricing/availability overlay — same pattern as the catalog's
  // ProductGrid: cards render immediately from cached pricing (org pricing via
  // the search API), and live Hajoca values win when they land. One batched
  // lookup per item, fired once its search settles; on error the cached
  // values simply remain.
  const { data: liveInventory } = useHajocaInventory(topProducts, {
    enabled: searchSettled && topProducts.length > 0,
  });
  const displayProducts = useMemo(() => {
    if (!liveInventory) return topProducts;
    return applyLiveInventory(topProducts, liveInventory.byProduct);
  }, [topProducts, liveInventory]);

  // The auto-select below usually fires before live pricing lands, so re-emit
  // the selection with the live unitPrice once it arrives — the order summary
  // must match what the cards show.
  useEffect(() => {
    if (!liveInventory || !selectedProductId || !onSelectedProductChange) return;
    const selected = displayProducts.find((p) => p.uid === selectedProductId);
    if (!selected) return;
    onSelectedProductChange(
      selectedProductId,
      quantity,
      selected.currentPrice ?? undefined,
      searchId,
      activeSearchTerm,
    );
    // Only when live data lands — the other values are already current then.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveInventory]);

  // Recompute paging affordances + mobile height whenever the result set or
  // viewport changes. A ResizeObserver catches card growth as images load and
  // breakpoint switches; scroll events alone don't fire on first render.
  useEffect(() => {
    measureStrip();
    const el = scrollStripRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => measureStrip());
    ro.observe(el);
    Array.from(el.children).forEach((c) => ro.observe(c));
    window.addEventListener("resize", measureStrip);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", measureStrip);
    };
  }, [displayProducts, measureStrip]);

  // Notify parent when products are loaded and there are no results. When
  // re-ranking is on, wait until this item's turn has settled so waiting items
  // aren't reported as "no products" before they've actually searched.

  useEffect(() => {
    if (searchSettled && activeSearchTerm && onNoProductsFound) {
      const hasNoProducts =
        !products || !Array.isArray(products) || products.length === 0;
      onNoProductsFound(hasNoProducts);
    }
  }, [products, searchSettled, activeSearchTerm, onNoProductsFound]);

  // Auto-select first product when products load
  useEffect(() => {
    if (displayProducts.length > 0 && !selectedProductId && !productsLoading) {
      const firstProduct = displayProducts[0];
      setSelectedProductId(firstProduct.uid);
      onItemChange({
        ...item,
        selectedProductId: firstProduct.uid,
      });
      if (onSelectedProductChange) {
        onSelectedProductChange(
          firstProduct.uid,
          quantity,
          firstProduct.currentPrice ?? undefined,
          searchId,
          activeSearchTerm
        );
      }
    }
  }, [
    displayProducts,
    selectedProductId,
    productsLoading,
    item,
    quantity,
    onItemChange,
    onSelectedProductChange,
    searchId,
    activeSearchTerm,
  ]);

  const handleQuantityChange = useCallback(
    (newQuantity: number) => {
      capture({
        event: "list_item_edited",
        context: { listSessionId, itemId: item.id, itemIndex: index, field: "quantity" },
      });
      setQuantity(newQuantity);
      onItemChange({
        ...item,
        quantity: newQuantity,
      });
      // Update summary when quantity changes
      if (selectedProductId && onSelectedProductChange) {
        const selectedProduct = displayProducts.find(
          (p) => p.uid === selectedProductId
        );
        if (selectedProduct) {
          onSelectedProductChange(
            selectedProductId,
            newQuantity,
            selectedProduct.currentPrice ?? undefined,
            searchId,
            activeSearchTerm
          );
        }
      }
    },
    [
      item,
      onItemChange,
      selectedProductId,
      displayProducts,
      onSelectedProductChange,
      capture,
      listSessionId,
      index,
      searchId,
      activeSearchTerm,
    ]
  );

  const handleSearchInputChange = useCallback((newSearchInput: string) => {
    setSearchInput(newSearchInput);
    // Editing dismisses the correction banner and lets the next response
    // correct the box again.
    setCorrectionOriginal(null);
    setSkipCorrection(false);
    appliedCorrectionRef.current = null;
    // Rewrite fraction shorthand once the typing settles.
    fraction.handleInputChange(newSearchInput);
  }, [fraction]);

  const performSearch = useCallback(() => {
    // Committing the search finishes the word being typed, so a fraction
    // rewrite the debounce was holding back applies now. Either way the box
    // and the search end up on the same text.
    const trimmedSearch = fraction.commit(searchInput).trim();

    // The box only differs because the card wrote the server's correction
    // into it, so there is nothing new to search.
    if (trimmedSearch === appliedCorrectionRef.current) return;

    if (trimmedSearch && trimmedSearch !== item.searchTerm) {
      setIsUserEditedSearch(true);
      capture({
        event: "list_item_edited",
        context: {
          listSessionId,
          itemId: item.id,
          itemIndex: index,
          field: "searchTerm",
          previousQuery: item.searchTerm,
          newQuery: trimmedSearch,
        },
      });
    }

    // A changed term (including cleared to empty) drops the row's selection in
    // the parent immediately — the product picked for the old term must not
    // ride into an order started while the new search is still running.
    if (trimmedSearch !== item.searchTerm) {
      onSelectedProductChange?.("", quantity);
    }

    setActiveSearchTerm(trimmedSearch);
    setSelectedProductId(undefined); // Reset selection when search changes
    onItemChange({
      ...item,
      searchTerm: trimmedSearch,
      selectedProductId: undefined,
    });
  }, [searchInput, item, quantity, onItemChange, onSelectedProductChange, capture, index, fraction]);

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        performSearch();
      }
    },
    [performSearch]
  );

  const handleProductClick = useCallback(
    (product: Product) => {
      const rank = topProducts.findIndex((p) => p.uid === product.uid);
      capture({
        event: "list_product_selected",
        context: {
          listSessionId,
          itemId: item.id,
          itemIndex: index,
          productId: product.uid,
          rank,
          searchId,
          query: activeSearchTerm,
        },
      });
      setSelectedProductId(product.uid);
      onItemChange({
        ...item,
        selectedProductId: product.uid,
      });
      if (onSelectedProductChange) {
        onSelectedProductChange(
          product.uid,
          quantity,
          product.currentPrice ?? undefined,
          searchId,
          activeSearchTerm
        );
      }
    },
    [
      item,
      quantity,
      onItemChange,
      onSelectedProductChange,
      topProducts,
      capture,
      listSessionId,
      index,
      searchId,
      activeSearchTerm,
    ]
  );

  return (
    <Card className="relative">
      {canRemove && onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-2 right-2 h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(index)}
          aria-label={t("pages.list.removeItem")}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
      <CardContent className="p-6">
        <div className="flex flex-col md:flex-row gap-6">
          {/* Left side: Quantity and Search Fields */}
          <div className="md:w-1/3 space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.list.quantity")}</label>
              <QuantityInput
                value={quantity}
                onChange={handleQuantityChange}
                min={1}
                max={9999}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("pages.list.search")}</label>
              <div className="flex gap-2">
                <Input
                  type="text"
                  value={searchInput}
                  onChange={(e) => handleSearchInputChange(e.target.value)}
                  onKeyDown={handleSearchKeyDown}
                  placeholder={t("pages.list.enterSearchTerm")}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={performSearch}
                  size="icon"
                  className="h-10 w-10"
                >
                  <Search className="h-4 w-4" />
                </Button>
              </div>
              {/* Correction banner, shown instead of the fraction one when
                  both apply. */}
              {correctionOriginal !== null && (
                <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                  <span className="text-muted-foreground">
                    {t("components.products.productSearchInput.keep")}{" "}
                  </span>
                  <button
                    type="button"
                    onClick={handleRevertCorrection}
                    className="text-blue-600 hover:text-blue-800 font-medium underline"
                  >
                    {correctionOriginal}
                  </button>
                  <span className="text-muted-foreground">
                    {" "}
                    {t("components.products.productSearchInput.asYourSearchTerm")}
                  </span>
                </div>
              )}
              {/* Fraction banner, offering the typed text back. */}
              {correctionOriginal === null && fraction.original !== null && (
                <div className="px-3 py-2 bg-blue-50 border border-blue-200 rounded-md text-sm">
                  <span className="text-muted-foreground">
                    {t("components.products.productSearchInput.keep")}{" "}
                  </span>
                  <button
                    type="button"
                    onClick={fraction.revert}
                    className="text-blue-600 hover:text-blue-800 font-medium underline"
                  >
                    {fraction.original}
                  </button>
                  <span className="text-muted-foreground">
                    {" "}
                    {t("components.products.productSearchInput.asYourSearchTerm")}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Right side: Product Cards */}
          <div className="md:w-2/3">
            <h3 className="text-lg font-semibold mb-4">{t("pages.list.topResults")}</h3>
            {showActiveRerank || showWaitingRerank ? (
              <AiRerankLoadingLto />
            ) : showSearchAiRerank ? (
              <AiRerankLoading />
            ) : productsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary" />
              </div>
            ) : displayProducts.length > 0 ? (
              // Show ~3 products at a time; the rest (up to RESULTS_PER_ITEM)
              // are revealed by clicking the paging arrows — horizontally on
              // web, vertically on mobile. The next-arrow doubles as a "More"
              // affordance.
              <div className="relative">
                <div
                  ref={scrollStripRef}
                  onScroll={measureStrip}
                  style={{ maxHeight: stripMaxHeight }}
                  className="flex flex-col md:flex-row gap-4 md:items-stretch overflow-hidden p-2 -m-2 scrollbar-none"
                >
                  {displayProducts.map((product: Product) => {
                    // Results are always LLM re-ranked, so the low-score warning
                    // (a plain-FTS-only affordance) never applies here.
                    return (
                      <div
                        key={product.uid}
                        onClick={() => handleProductClick(product)}
                        className="cursor-pointer flex w-full md:w-[31%] md:flex-shrink-0"
                      >
                        <ProductCatalogCard
                          product={product}
                          quantity={0}
                          onQuantityChange={() => {}}
                          disabled={false}
                          isHighlighted={selectedProductId === product.uid}
                          isLowScore={false}
                          onCardClick={() => handleProductClick(product)}
                          showCachedPricing={true}
                          hideQuantityControls={true}
                          showDetailsButton={true}
                          className="flex-1"
                        />
                      </div>
                    );
                  })}
                </div>

                {/* Previous (left on web / up on mobile) */}
                {canScrollPrev && (
                  <button
                    type="button"
                    aria-label={t("components.ui.pagination.previous")}
                    onClick={() => pageStrip(-1)}
                    className="absolute z-10 flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white/90 text-gray-600 shadow-md backdrop-blur transition-colors hover:bg-white hover:text-gray-900 active:scale-95
                      left-1/2 -translate-x-1/2 top-0 -mt-1
                      md:left-0 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:mt-0"
                  >
                    <ChevronUp className="h-5 w-5 md:hidden" />
                    <ChevronLeft className="hidden h-5 w-5 md:block" />
                  </button>
                )}

                {/* Next (right on web / down on mobile) — also the "More" hint */}
                {canScrollNext && (
                  <button
                    type="button"
                    aria-label={t("pages.list.moreProducts")}
                    onClick={() => pageStrip(1)}
                    className="absolute z-10 flex items-center gap-1 rounded-full border border-gray-200 bg-white/90 px-2 text-gray-600 shadow-md backdrop-blur transition-colors hover:bg-white hover:text-gray-900 active:scale-95
                      h-9
                      left-1/2 -translate-x-1/2 bottom-0 -mb-1
                      md:left-auto md:right-0 md:top-1/2 md:translate-x-1/2 md:-translate-y-1/2 md:bottom-auto md:mb-0"
                  >
                    <span className="text-xs font-medium">{t("pages.list.moreProducts")}</span>
                    <ChevronDown className="h-5 w-5 md:hidden" />
                    <ChevronRight className="hidden h-5 w-5 md:block" />
                  </button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>
                  {activeSearchTerm.trim().length >= 2
                    ? `No products found for "${displaySearchTerm}"`
                    : activeSearchTerm.trim().length > 0
                      ? t("pages.list.searchTermMustBe")
                      : t("pages.list.enterASearchTerm")}
                </p>
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
